import DOMPurify, { type Config } from "dompurify";
import { marked } from "marked";

/**
 * Render authored KB markdown to HTML safe to inject.
 *
 * The database stores the source verbatim. This is the only HTML gate: the
 * reader and the admin preview both call this function. Parse with marked,
 * then sanitise with a dedicated DOMPurify instance. Never pass the marked
 * string to the DOM without that pass, and never reorder the two calls.
 *
 * The allowlist is explicit. DOMPurify's defaults also permit style, form,
 * button, textarea, select, svg, math, id, popover, and srcset. CSP
 * style-src includes 'unsafe-inline', so a style attribute or a style
 * element would paint. input is allowed only so a GFM task-list checkbox
 * can render; the hook drops every other input and forces disabled.
 *
 * class is kept only on code, and only for a fenced-code language token
 * (language-python). Any other class is removed, so an author cannot attach
 * app classes such as modal-backdrop or slideover.
 *
 * Tags: p, br, hr, span, div, h1, h2, h3, h4, h5, h6, strong, b, em, i, u,
 * s, del, mark, sub, sup, ul, ol, li, a, img, code, pre, kbd, samp,
 * blockquote, details, summary, table, thead, tbody, tr, th, td, caption,
 * input, dl, dt, dd, abbr, small, ins, q, cite, figure, figcaption.
 *
 * Attributes: href, title, src, alt, class, open, align, start, type,
 * checked, disabled, width, height, colspan, rowspan.
 * ALLOW_DATA_ATTR and ALLOW_ARIA_ATTR are false, so this list is exact.
 *
 * URLs must match ALLOWED_URI_REGEXP (http, https, mailto, tel, plus
 * relative and fragment links). DOMPurify still re-allows a data: src on
 * img after that regexp rejects it. The attribute hook keeps data: on an
 * img src only when it is data:image/(png|jpeg|gif|webp);base64,. It drops
 * data:image/svg+xml and every data: href.
 */

const ALLOWED_TAGS = [
  "p", "br", "hr", "span", "div",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "strong", "b", "em", "i", "u", "s", "del", "mark", "sub", "sup",
  "ul", "ol", "li",
  "a", "img",
  "code", "pre", "kbd", "samp",
  "blockquote",
  "details", "summary",
  "table", "thead", "tbody", "tr", "th", "td", "caption",
  "input",
  "dl", "dt", "dd", "abbr", "small", "ins", "q", "cite", "figure", "figcaption",
];

const ALLOWED_ATTR = [
  "href", "title", "src", "alt", "class", "open",
  "align", "start", "type", "checked", "disabled",
  "width", "height", "colspan", "rowspan",
];

// http(s), mailto, and tel, or a URL that does not start a scheme (relative,
// fragment). data: and javascript: do not match. [a-z0-9] is required so
// "data:" is not treated as a scheme-less token.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z0-9+.\-]+(?:[^a-z0-9+.\-:]|$))/i;

// Screenshot embeds. svg+xml is not in this set. Matched against the
// whitespace-stripped, lowercased attribute value.
const SAFE_IMAGE_DATA = /^data:image\/(png|jpeg|gif|webp);base64,/;
const LANGUAGE_CLASS = /^language-[\w-]+$/;

const RENDER_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

// Own instance so these hooks never apply to another DOMPurify caller.
const purify = DOMPurify(window);

purify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName !== "input" || !(node instanceof Element)) return;
  const type = (node.getAttribute("type") ?? "").toLowerCase();
  if (type !== "checkbox") {
    node.remove();
    return;
  }
  node.setAttribute("disabled", "");
});

purify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "class") {
    const tag = node instanceof Element ? node.tagName.toLowerCase() : "";
    if (tag === "code" && LANGUAGE_CLASS.test(data.attrValue)) return;
    data.keepAttr = false;
    return;
  }
  if (data.attrName !== "src" && data.attrName !== "href") return;
  const compact = data.attrValue.replace(/[\u0000-\u0020]+/g, "").toLowerCase();
  if (!compact.startsWith("data:")) return;
  const tag = node instanceof Element ? node.tagName.toLowerCase() : "";
  if (data.attrName === "src" && tag === "img" && SAFE_IMAGE_DATA.test(compact)) return;
  data.keepAttr = false;
});

export function renderKbMarkdown(body: string | null | undefined): string {
  if (!body) return "";
  const raw = marked.parse(body, { async: false }) as string;
  return purify.sanitize(raw, RENDER_CONFIG);
}
